import React from 'react'
import { SharePanel } from '@mote/presentation'
import { DashboardLayout } from '../../../layouts'

import Paginator from '../../../shared/Paginator'
import {
  noLog as log,
  EpochToPrettyDate,
  getGodModeOptions,
  EpochToAgo
} from '../../../shared/Utils'
import { reallyTruthy } from '@mote/helpers/validation'
import { Visibility } from './Visibility'

import { REACTION_EMOJIS } from '@mote/helpers/constants'
import { Block, Button, Tooltip, Text, Spinner } from '@mote/ui'
import { CloseIcon, EyeIcon, HeadsetIcon, ShareIcon } from '@mote/assets/icons'
import styled from 'styled-components'
import { getApplicationName } from 'plugin/src/helpers/getApplicationName'
import { Content } from '../../Dashboard/components'
import { useActivityQuery } from '@mote/api/src/requests/activity'
import { useTrackMutation } from '@mote/api'

const queryString = require('query-string')

const PLAYER_ID = 'audio-player'
const CLASSROOM_PARAMS = {
  c: { param: 'className', name: 'Class name' },
  s: { param: 'studentName', name: 'Student name' },
  a: { param: 'assignmentName', name: 'Assignment name' }
}

function lookupEmoji(name) {
  for (let item of REACTION_EMOJIS) {
    if (item.name === name) {
      return item
    }
  }
  return null
}

export function ActivityScreen({ match }) {
  const [state, setState] = React.useState({
    activity: [],
    pagination: null,
    showSharePanel: null
  })
  const { mutate: track } = useTrackMutation()
  const godModeOptions = getGodModeOptions()
  const queryParams = queryString.parse(window.location.search)
  let hasFacetOrFilter = false
  for (let key of ['e', 'c', 's', 'a']) {
    if (queryParams[key] !== undefined) {
      hasFacetOrFilter = true
      break
    }
  }

  const pageIndex = React.useMemo(() => {
    const tmpPageIndex = parseInt(match.params.pageIndex)
    return !Number.isNaN(tmpPageIndex) ? tmpPageIndex : 0
  }, [match])

  const { mutate: getActivity, loading, error } = useActivityQuery()

  React.useEffect(() => {
    _fetchActivity()
  }, [])

  function _renderActivity() {
    return (
      <Content title='My Activity'>
        {loading && (
          <Block justify='center' align='center'>
            <Spinner radius={45} />
          </Block>
        )}

        {error && (
          <div>
            Sorry, we couldn't load your activity data. Please try again later.
          </div>
        )}

        {state.activity.length > 0 && (
          <>
            <Block layout='horizontal' justify='space-between' align='center'>
              {/* trick into center the paginator*/}
              <div></div>
              {_renderPaginator()}
              {_renderEngagementFilter()}
            </Block>
            <Block>
              {_renderFacetOrFilter()}
              {_renderActivityTable()}
              <Block layout='horizontal' justify='center'>
                {_renderPaginator()}
              </Block>
            </Block>
          </>
        )}

        {state.activity.length === 0 && !loading && !error && (
          <span>
            No activity yet. When you record motes you can see them here.
          </span>
        )}
      </Content>
    )
  }

  function _renderPaginator() {
    if (!state.pagination) {
      return null
    }
    return (
      <Tooltip content='Browse your motes reverse chronologically'>
        <Paginator
          baseURL='/account/activity/'
          pageIndex={pageIndex}
          pageIndexes={state.pagination.pageIndexes}
        />
      </Tooltip>
    )
  }

  function _renderFacetOrFilter() {
    if (!hasFacetOrFilter) {
      return <React.Fragment />
    }
    const q = queryParams
    return (
      <>
        <b>Filtered by:</b>
        <Block layout='horizontal' gap={20} margin='10px 0 20px 0'>
          {q.c !== undefined && _renderFacetOrFilterLink('c', 'Class', q.c)}
          {q.a !== undefined &&
            _renderFacetOrFilterLink('a', 'Assignment', q.a)}
          {q.s !== undefined && _renderFacetOrFilterLink('s', 'Student', q.s)}
          {q.e !== undefined &&
            _renderFacetOrFilterLink(
              'e',
              'moticed',
              reallyTruthy(q.e) ? 'yes' : 'no'
            )}
        </Block>
      </>
    )
  }

  function _renderFacetOrFilterLink(param, dimension, value) {
    return (
      <Block layout='horizontal'>
        <span>
          {dimension} = {value}{' '}
        </span>
        <Tooltip content='Clear filter'>
          <a
            className='activity-clear'
            href={_makeURLWithParams(param, undefined)}
          >
            <CloseIcon />
          </a>
        </Tooltip>
      </Block>
    )
  }

  function _renderEngagementFilter() {
    return (
      <Tooltip content="Filter by mote engagement - has it been 'moticed' yet?">
        <span>
          {_renderEngagementLink('All', undefined)} |{' '}
          {_renderEngagementLink('moticed', '1')} |{' '}
          {_renderEngagementLink('unmoticed', '0')}
        </span>
      </Tooltip>
    )
  }

  function _renderEngagementLink(label, value) {
    if (queryParams.e === value) {
      return <React.Fragment>{label}</React.Fragment>
    } else {
      return (
        <Button
          variant='link'
          tag='a'
          text={label}
          href={_makeURLWithParams('e', value)}
        />
      )
    }
  }

  function handleMoteShare(moteId) {
    setState(prev => ({ ...prev, showSharePanel: moteId }))
    track({ variables: { event: 'Mote Share Clicked', location: 'web' } })
  }

  function _renderActivityTable() {
    return (
      <Table>
        <thead>
          <tr>
            <th className='activity-asset' scope='col'>
              <p className='lead activity-table-header'>Voice note</p>
            </th>
            <th className='activity-created' scope='col'>
              <p className='lead activity-table-header'>Created</p>
            </th>
            <th className='activity-status' scope='col'>
              <p className='lead activity-table-header'>Availability</p>
            </th>
            <th scope='col'>
              <p className='lead activity-table-header'>Listen or download</p>
            </th>
          </tr>
        </thead>

        <tbody>
          {state.activity.map(a => {
            var peopleRows = _crunchInteractionsIntoPeopleRows(a)
            // log("procesing node:", a);
            var studentish = _isReallyForStudent(a)
            return (
              <tr key={a.moteId}>
                <td className='activity-row'>
                  <Block gap={10}>
                    <Text tag='p'>{a.niceTitle}</Text>
                    {peopleRows.map(pr => {
                      // console.log("evaluating ... ", pr);
                      return (
                        <Block
                          layout='horizontal'
                          key={Math.random()}
                          className='row activity-user'
                          gap={10}
                          align='center'
                        >
                          <Tooltip content='Audience name' direction='right'>
                            <span>{pr.name}</span>
                          </Tooltip>
                          <Block layout='horizontal' align='center'>
                            {pr.view && (
                              <Tooltip content='Opened or viewed comment'>
                                <span>
                                  <EyeIcon />
                                </span>
                              </Tooltip>
                            )}
                            {pr.listen && (
                              <Tooltip content='Listened to comment'>
                                <span>
                                  <HeadsetIcon />
                                </span>
                              </Tooltip>
                            )}
                            {pr.react && pr.react.length > 0 && (
                              <React.Fragment>
                                {pr.react.map(item => {
                                  const emoji = lookupEmoji(item)
                                  if (!emoji) {
                                    return <React.Fragment />
                                  } else {
                                    return (
                                      <Tooltip
                                        key={Math.random()}
                                        content={emoji.message}
                                      >
                                        <span
                                          role='img'
                                          className='engagement-emoji'
                                          aria-label={
                                            'Feedback - ' + emoji.message
                                          }
                                        >
                                          {emoji.emoji}
                                        </span>
                                      </Tooltip>
                                    )
                                  }
                                })}
                              </React.Fragment>
                            )}
                          </Block>
                        </Block>
                      )
                    })}

                    {a.transcriptDisplay && (
                      <TranscriptDisplay>
                        {a.transcriptDisplay}
                      </TranscriptDisplay>
                    )}

                    {(a.className || a.assignmentName || studentish) && (
                      <Block gap={5}>
                        {a.className && (
                          <Tooltip content='Filter by class'>
                            <Button
                              variant='link'
                              tag='a'
                              text={a.className}
                              href={_makeURLWithParams('c', a.className, true)}
                            />{' '}
                          </Tooltip>
                        )}
                        {a.assignmentName && (
                          <Tooltip content='Filter by assignment'>
                            <Button
                              variant='link'
                              tag='a'
                              text={a.assignmentName}
                              href={_makeURLWithParams(
                                'a',
                                a.assignmentName,
                                true
                              )}
                            />{' '}
                          </Tooltip>
                        )}
                        {studentish && (
                          <Tooltip content='Filter by student'>
                            <Button
                              variant='link'
                              tag='a'
                              text={a.studentName}
                              href={_makeURLWithParams(
                                's',
                                a.studentName,
                                true
                              )}
                            />{' '}
                          </Tooltip>
                        )}
                      </Block>
                    )}

                    <div>
                      {(a.canRedirect || a.canLink) && (
                        <>
                          <Button
                            variant='link'
                            tag='a'
                            text='View original context'
                            href={
                              a.canRedirect ? '/m/' + a.moteId : a.containingURL
                            }
                            target='_blank'
                            rel='noopener noreferrer'
                          />{' '}
                          |{' '}
                        </>
                      )}
                      <Button
                        variant='link'
                        tag='a'
                        text='View landing page'
                        href={`/m/${a.moteId}${
                          a.canRedirect ? '?skipRedirect=true' : ''
                        }`}
                        target='_blank'
                        rel='noopener noreferrer'
                        testId='view-landing-page-button'
                      />
                    </div>

                    <Block align='flex-start'>
                      <Button
                        size='sm'
                        icon={<ShareIcon color='white' />}
                        text='Share'
                        onClick={_ => handleMoteShare(a.moteId)}
                      />
                      {state.showSharePanel &&
                        state.showSharePanel === a.moteId && (
                          <SharePanel
                            moteId={a.moteId}
                            canRedirect={a.canRedirect}
                            renderFor='web'
                            onClose={_ =>
                              setState(prev => ({
                                ...prev,
                                showSharePanel: null
                              }))
                            }
                          />
                        )}
                    </Block>
                  </Block>
                </td>
                <td className='activity-row-ago'>
                  <p>
                    {EpochToAgo(a.created)}
                    <br></br>
                    <small>{EpochToPrettyDate(a.created)}</small>
                  </p>
                </td>

                <td className='activity-row'>
                  <Visibility
                    activity={a}
                    onChange={() => {}}
                    onDelete={() => {
                      _fetchActivity()
                    }}
                  />
                </td>
                <td className='activity-row'>
                  {' '}
                  <audio
                    id={PLAYER_ID}
                    controls
                    style={{ outline: 'none' }}
                    autoPlay={false}
                    src={a.playbackLocation}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    )
  }

  function _isReallyForStudent(a) {
    if (a.studentName && getApplicationName(a.containingURL)) {
      if (
        a.containingURL.indexOf('/student/') > -1 ||
        a.containingURL.indexOf('/tg/') > -1
      ) {
        return true
      }
    }
    return false
  }

  //////////////////////////// non-rendering methods //////////////////////////

  function _fetchActivity(hasAuthenticated, count) {
    if (!count) {
      count = 0
    }

    const filtersAndFacets = {}
    if (queryParams.e !== undefined) {
      filtersAndFacets['wasEngagedWith'] = reallyTruthy(queryParams.e)
    }

    for (let p of Object.keys(CLASSROOM_PARAMS)) {
      if (queryParams[p] !== undefined) {
        filtersAndFacets['facetName'] = CLASSROOM_PARAMS[p].param
        filtersAndFacets['facetValue'] = queryParams[p]
        break
      }
    }
    log('found filters and facets: ', filtersAndFacets)
    const params = Object.assign({}, godModeOptions, {
      pageIndex: pageIndex,
      ...filtersAndFacets
    })

    getActivity({ variables: params }).then(res => {
      if (res.error) return

      setState(prev => ({
        ...prev,
        activity: res.data.activity,
        pagination: res.data.pagination
      }))
    })
  }

  function _crunchInteractionsIntoPeopleRows(activityRow) {
    const interactions = activityRow.interactions
    if (!interactions || interactions === {}) {
      return []
    }
    var persons = {}
    for (const key of ['view', 'listen', 'react']) {
      try {
        for (const row of interactions[key]) {
          var id, name, timestamp
          var o = {}
          if (row.userId) {
            id = row.userId
            name = row.userDisplayName
          } else {
            id = row.deviceId
            name = 'Someone'
          }
          timestamp = Date.parse(row.created)
          if (persons[id]) {
            o = persons[id]
            if (timestamp > o.timestamp) {
              o.timestamp = timestamp
            }
          } else {
            o = {
              id: id,
              name: name,
              timestamp: timestamp,
              view: false,
              listen: false,
              react: []
            }
          }
          if (key === 'react') {
            o.react.push(row.reaction)
            o.view = true
          } else if (key === 'listen') {
            o.listen = true
            o.view = true
          } else if (key === 'view') {
            o.view = true
          }
          persons[id] = o
        }
      } catch (e) {
        // error("error building rows for ", e, key, interactions);
      }
    }
    var personRows = []
    for (const key of Object.keys(persons)) {
      personRows.push(persons[key])
    }
    personRows.sort((a, b) => {
      return b.timestamp - a.timestamp
    })
    return personRows
  }

  function _makeURLWithParams(key, value, scrubClassroom) {
    const targetParams = Object.assign({}, queryParams)
    if (scrubClassroom) {
      delete targetParams.a
      delete targetParams.c
      delete targetParams.s
    }
    if (value === undefined) {
      delete targetParams[key]
    } else {
      targetParams[key] = value
    }
    const kvPairs = []
    for (let k of Object.keys(targetParams)) {
      kvPairs.push(k + '=' + encodeURIComponent(targetParams[k]))
    }
    var result =
      '/account/activity/0' + (kvPairs.length ? '?' + kvPairs.join('&') : '')
    log(`calculated URL for ${key} and ${value}: ${result}`)
    return result
  }

  return <DashboardLayout>{_renderActivity()}</DashboardLayout>
}

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 20px;

  td {
    padding: 12px 14px;
    border-top: 1px solid #dee2e6;
    text-align: left;
    line-height: 16px;
    color: #646f7b;
  }

  thead th {
    border: 0;
    vertical-align: bottom;
  }

  tbody tr {
    &:hover {
      background: rgba(213, 212, 218, 0.25);
    }
  }

  th {
    text-align: left;
    padding: 22px 14px;
    font-weight: 400;
    border-top: 1px solid #dee2e6;
    background-color: #f9f9fb;
  }
`

const TranscriptDisplay = styled.p`
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  -webkit-box-orient: vertical;
  font-size: 13px;
`
